import { defineEntityContract, type EntityByIdRequests, type EntityStateEvents, type QueuedChangeFailureEvents } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { InspectionInDto } from 'shared/dtos/in/inspection';
import type { InspectionOutDto } from 'shared/dtos/out/inspection';
import type { ProtocolAnswerUpdateOutDto, ProtocolSectionAnswersBatchOutDto } from 'shared/dtos/out/protocolAnswer';
import type { ProtocolItemOutDto } from 'shared/dtos/out/protocolItem';
import type { ProtocolSectionOutDto } from 'shared/dtos/out/protocolSection';
import type { InspectionModel } from './inspectionModel';

/** A change of an inspection of the project; `uniqueId` correlates its outcome event. */
interface InspectionChange {
  inspectionId: string;
  projectId: string;
  uniqueId: string;
}

/** A change of one protocol item (the filled checklist item) of an inspection. */
interface ProtocolItemChange extends InspectionChange {
  protocolItemId: string;
}

/** A change of one protocol section of an inspection. */
interface ProtocolSectionChange extends InspectionChange {
  sectionId: string;
}

/**
 * Inspection events. Inspection and protocol changes share `updatedEntity` and
 * are told apart only by their `uniqueId`: it carries the server's answer to
 * an edit, a protocol item, answer or section change, a section answers batch
 * or a restore, so it stays `unknown` (the client narrows its own outcome).
 */
export type InspectionEvents = EntityStateEvents & QueuedChangeFailureEvents & {
  [DomainMessagesTypes.allInspections]: { hasAll?: boolean; items: InspectionModel[] };
  [DomainMessagesTypes.uploadedEntity]: InspectionInDto;
  [DomainMessagesTypes.updatedEntity]: unknown;
  [DomainMessagesTypes.deletedEntity]: unknown;
};

/** Inspection and protocol changes; each resolves once queued, its outcome follows as an event. */
export type InspectionRequests = EntityByIdRequests<InspectionModel> & {
  create: {
    request: { templateId: string; inspectionOutDto: InspectionOutDto; projectId: string; uniqueId: string };
    response: void;
  };
  edit: { request: InspectionChange & { inspectionOutDto: Partial<InspectionOutDto> }; response: void };
  remove: { request: InspectionChange; response: void };
  restore: { request: InspectionChange; response: void };
  editProtocolItem: { request: ProtocolItemChange & { protocolItemOutDto: ProtocolItemOutDto }; response: void };
  editProtocolAnswer: {
    request: ProtocolItemChange & { protocolAnswerId: string; protocolAnswerOutDto: ProtocolAnswerUpdateOutDto };
    response: void;
  };
  editProtocolSection: {
    request: ProtocolSectionChange & { protocolSectionOutDto: ProtocolSectionOutDto };
    response: void;
  };
  /** Sets the applicability of every answer in the section tree (`protocolItemIds`). */
  editProtocolSectionAnswers: {
    request: ProtocolSectionChange & {
      protocolSectionAnswersBatchOutDto: ProtocolSectionAnswersBatchOutDto;
      protocolItemIds: string[];
    };
    response: void;
  };
};

export const inspectionContract = defineEntityContract<InspectionModel, InspectionEvents, InspectionRequests>(
  'inspectionChannel',
  { collectionEvent: DomainMessagesTypes.allInspections }
);
